import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

const PROMOTION_CODE = "HUSH10";
const PROMOTION_RATE = 0.1;

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const [promoInput, setPromoInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [promoMessage, setPromoMessage] = useState("");

  const lines = useMemo(
    () =>
      cartItems.reduce((items, item) => {
        const product = productData.find(
          (candidate) => Number(candidate.id) === Number(item.id)
        );

        if (!product) return items;

        const quantity = Math.max(1, Number(item.quantity) || 1);

        return [
          ...items,
          {
            ...item,
            quantity,
            product,
            unitPrice: Number(product.price),
            lineTotal: Number(product.price) * quantity,
          },
        ];
      }, []),
    [cartItems]
  );

  const subtotal = lines.reduce((total, line) => total + line.lineTotal, 0);
  const itemCount = lines.reduce((total, line) => total + line.quantity, 0);
  const shipping = siteConfig.commerce.standardShipping;
  const discount = appliedCode ? subtotal * PROMOTION_RATE : 0;
  const total = Math.max(0, subtotal - discount + shipping);
  const shippingProgress = 100;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleQuantity = (line, quantity) => {
    updateQuantity(line.id, line.size, line.color, quantity);
  };

  const handlePromoSubmit = (event) => {
    event.preventDefault();
    const normalizedCode = promoInput.trim().toUpperCase();

    if (normalizedCode === PROMOTION_CODE) {
      setAppliedCode(PROMOTION_CODE);
      setPromoInput(PROMOTION_CODE);
      setPromoMessage("10% promotion applied.");
      return;
    }

    setAppliedCode("");
    setPromoMessage(
      normalizedCode
        ? "That promotion code is not available."
        : "Enter a promotion code first."
    );
  };

  const handleCheckout = () => {
    navigate("/checkout", {
      state: appliedCode
        ? {
            checkoutPromotion: {
              code: appliedCode,
              rate: PROMOTION_RATE,
            },
          }
        : undefined,
    });
    scrollToTop();
  };

  if (lines.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <section className="cart-empty" aria-labelledby="empty-cart-title">
          <p className="cart-kicker">YOUR BAG / 00</p>
          <div className="cart-empty__mark" aria-hidden="true">
            0
          </div>
          <h1 id="empty-cart-title">YOUR CART IS EMPTY.</h1>
          <p>
            Build your setup with performance keyboards, mice, and desk gear.
          </p>
          <Link className="cart-button cart-button--dark" to="/shop" onClick={scrollToTop}>
            SHOP ALL GEAR
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <div>
          <p className="cart-kicker">SECURE CART / READY TO CHECK OUT</p>
          <h1>YOUR BAG</h1>
        </div>
        <p className="cart-page__count">
          {String(itemCount).padStart(2, "0")} {itemCount === 1 ? "ITEM" : "ITEMS"}
        </p>
      </header>

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-label="Shopping cart items">
          <div className="cart-shipping-meter">
            <div className="cart-shipping-meter__copy">
              <strong>
                TRACKED SHIPPING · {formatPrice(shipping)}
              </strong>
              <span>{Math.round(shippingProgress)}%</span>
            </div>
            <div
              className="cart-shipping-meter__track"
              role="progressbar"
              aria-label="Progress toward free shipping"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={Math.round(shippingProgress)}
            >
              <span style={{ width: `${shippingProgress}%` }} />
            </div>
          </div>

          <div className="cart-table-labels" aria-hidden="true">
            <span>PRODUCT</span>
            <span>QUANTITY</span>
            <span>TOTAL</span>
          </div>

          <div className="cart-items">
            {lines.map((line) => {
              const { product } = line;
              const lineKey = `${line.id}-${line.size}-${line.color}`;

              return (
                <article className="cart-item" key={lineKey}>
                  <Link
                    className="cart-item__media"
                    to={`/shopDetails/${product.id}`}
                    onClick={scrollToTop}
                    aria-label={`View ${product.name}`}
                  >
                    <img
                      src={product.images?.[0] || product.image}
                      alt={product.name}
                    />
                  </Link>

                  <div className="cart-item__details">
                    <p className="cart-item__category">{product.category}</p>
                    <h2>
                      <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
                        {product.name}
                      </Link>
                    </h2>
                    <p className="cart-item__unit-price">{formatPrice(line.unitPrice)}</p>
                    <dl className="cart-item__options">
                      {line.size && (
                        <div>
                          <dt>CONFIG</dt>
                          <dd>{line.size}</dd>
                        </div>
                      )}
                      {line.color && (
                        <div>
                          <dt>FINISH</dt>
                          <dd>{line.color}</dd>
                        </div>
                      )}
                    </dl>
                    <button
                      className="cart-item__remove"
                      type="button"
                      onClick={() =>
                        removeFromCart(line.id, line.size, line.color)
                      }
                    >
                      REMOVE
                    </button>
                  </div>

                  <div className="cart-item__quantity">
                    <button
                      type="button"
                      onClick={() => handleQuantity(line, line.quantity - 1)}
                      aria-label={`Decrease ${product.name} quantity`}
                    >
                      −
                    </button>
                    <span aria-live="polite">{line.quantity}</span>
                    <button
                      type="button"
                      disabled={line.quantity >= product.inventory}
                      onClick={() => handleQuantity(line, line.quantity + 1)}
                      aria-label={`Increase ${product.name} quantity`}
                    >
                      +
                    </button>
                  </div>

                  <p className="cart-item__line-total">
                    {formatPrice(line.lineTotal)}
                  </p>
                </article>
              );
            })}
          </div>

          <Link className="cart-back-link" to="/shop" onClick={scrollToTop}>
            <span aria-hidden="true">←</span> CONTINUE SHOPPING
          </Link>
        </section>

        <aside className="cart-summary" aria-labelledby="cart-summary-title">
          <div className="cart-summary__inner">
            <p className="cart-kicker">ORDER / ESTIMATE</p>
            <h2 id="cart-summary-title">SUMMARY</h2>

            <form className="cart-promo" onSubmit={handlePromoSubmit} noValidate>
              <label htmlFor="cart-promo-code">PROMOTION CODE</label>
              <div>
                <input
                  id="cart-promo-code"
                  type="text"
                  value={promoInput}
                  onChange={(event) => {
                    setPromoInput(event.target.value);
                    setPromoMessage("");
                    if (appliedCode) setAppliedCode("");
                  }}
                  placeholder="ENTER CODE"
                  autoComplete="off"
                  aria-describedby="cart-promo-message"
                />
                <button type="submit">APPLY</button>
              </div>
              <p
                id="cart-promo-message"
                className={appliedCode ? "is-success" : ""}
                aria-live="polite"
              >
                {promoMessage || "Promotion code: HUSH10"}
              </p>
            </form>

            <div className="cart-summary__rows">
              <div>
                <span>SUBTOTAL</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="cart-summary__discount">
                  <span>PROMOTION ({appliedCode})</span>
                  <span>−{formatPrice(discount)}</span>
                </div>
              )}
              <div>
                <span>EST. SHIPPING</span>
                <span>{shipping === 0 ? "FREE" : formatPrice(shipping)}</span>
              </div>
            </div>

            <div className="cart-summary__total">
              <span>TOTAL</span>
              <strong>{formatPrice(total)}</strong>
            </div>
            <p className="cart-summary__note">
              Taxes and final delivery options are confirmed at checkout.
            </p>
            <button
              className="cart-button cart-button--accent"
              type="button"
              onClick={handleCheckout}
            >
              SECURE CHECKOUT <span aria-hidden="true">→</span>
            </button>
            <div className="cart-summary__assurance">
              <span>ENCRYPTED CHECKOUT</span>
              <span>30-DAY RETURNS</span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
